import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import equal from 'fast-deep-equal';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../models';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../models/NameUtils';
import { useUserQueries, useUserQuery } from '../../../queries';
import { useSharedMailboxesQuery } from '../../../queries/User/useSharedMailboxes';
import { useConnectionTypes } from '../../../queries/ConnectionType/useConnectionTypes';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../components-v2/Combobox/ComboboxAvatarItem';
import { Dialog, DialogDescription, DialogHeader, DialogPopup, DialogTitle } from '../../components-v2/Dialog';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../components-v2/Dropdown';
import { baseStyleSheet } from '../../styles/styles';
import { styleSheet } from './styles';

export interface ISendFromDropdownProps {
	styles?: StyleDeclarationValue[];
	sendFromOption?: Api.ISendOnBehalfPermission;
	sendFromUserId?: string;
	allowedOptions?: Api.ISendEmailFrom[];
	allowedContactBasedOptions?: 'all' | 'none' | 'allWithoutCustomSendOnBehalfPermissions';
	hideOptionsWithFilters?: boolean;
	sendOnBehalfOf: (option?: Api.ISendOnBehalfPermission, user?: Api.IUser) => void;
	disabled?: boolean;
	canSendOnBehalf?: boolean;
	onRenderSendFromOptions?(): React.ReactNode;
	resourceSelectorId?: Api.ResourceSelectorId;
	sendFromEmailAddress?: string;
	onSendFromEmailAddressChange?: (emailAddress?: string) => void;
}

interface ISendOnBehalfOption {
	text: string;
	value: Api.ISendOnBehalfPermission;
	sendFromEmailAddress?: string;
	isSharedMailbox?: boolean;
	disabled?: boolean;
}

// Default selection; the backend 400s if it isn't the user's primary or shared address.
const getPrimarySendFromAddress = (user?: Api.IUser): string | undefined =>
	user?.connectedEmailAddress || user?.lastTestEmail?.emailAddress || user?.primaryEmail?.value || user?.email;

const getSendOptions = (
	props: ISendFromDropdownProps,
	userSession: Api.UserSessionContext,
	connectionTypes: Api.IConnectionType[],
	sharedMailboxes?: Api.ISharedMailboxCollection,
	primaryAddress?: string
) => {
	const { firstName, lastName } = userSession!.user!;

	const defaultPermissions = userSession.defaultSendOnBehalfPermissions;
	const sendOnBehalfOptions: ISendOnBehalfOption[] = [];
	// Primary and shared mailboxes all ride on the CurrentUser permission; only `sendFromEmailAddress` differs.
	const mailboxes = sharedMailboxes?.available === false ? [] : (sharedMailboxes?.mailboxes ?? []);

	const currentUserOption = defaultPermissions.find(x => x.sender.mode === Api.SendEmailFrom.CurrentUser);
	if (currentUserOption) {
		sendOnBehalfOptions.push({
			text: mailboxes.length > 0 ? `${firstName} ${lastName} - Primary` : `${firstName} ${lastName}`,
			value: currentUserOption,
			sendFromEmailAddress: mailboxes.length > 0 ? primaryAddress : undefined,
		});

		mailboxes.forEach(mailbox => {
			sendOnBehalfOptions.push({
				text: `${firstName} ${lastName} - ${mailbox.name || mailbox.emailAddress}`,
				value: currentUserOption,
				sendFromEmailAddress: mailbox.emailAddress,
				isSharedMailbox: true,
				// Unverified mailboxes are rejected server-side on accounts where the round-trip is the only proof.
				disabled: sharedMailboxes!.reconnectRequired || (sharedMailboxes!.verificationRequired && !mailbox.verified),
			});
		});
	}

	const contactOwnerOption = defaultPermissions.find(x => x.sender.mode === Api.SendEmailFrom.ContactOwner);
	if (
		(props.allowedContactBasedOptions === 'all' ||
			props.allowedContactBasedOptions === 'allWithoutCustomSendOnBehalfPermissions') &&
		contactOwnerOption
	) {
		sendOnBehalfOptions.push({
			text: 'Contact owner',
			value: contactOwnerOption,
		});
	}

	if (
		props.allowedContactBasedOptions === 'all' ||
		props.allowedContactBasedOptions === 'allWithoutCustomSendOnBehalfPermissions'
	) {
		if (defaultPermissions.find(x => x.sender?.mode === Api.SendEmailFrom.ConnectionType) && connectionTypes?.length) {
			connectionTypes.forEach(connectionType => {
				sendOnBehalfOptions.push({
					text: `${connectionType.label} for the recipient`,
					value: { sender: { mode: Api.SendEmailFrom.ConnectionType, connectionType: connectionType.value } },
				});
			});
		}
	}

	const isPolicySelector =
		props.resourceSelectorId === Api.ResourceSelectorId.PolicyRenew ||
		props.resourceSelectorId === Api.ResourceSelectorId.UpcomingPolicyRenewals ||
		props.resourceSelectorId === Api.ResourceSelectorId.NewPolicy;
	if (
		isPolicySelector &&
		(props.allowedContactBasedOptions === 'all' ||
			props.allowedContactBasedOptions === 'allWithoutCustomSendOnBehalfPermissions')
	) {
		// Show all three policy roles for renewal / new-policy flows regardless of whether the account
		// has explicit defaultSendOnBehalfPermissions for them. The server implements each mode
		// unconditionally; prefer the real permission when present so any custom filters ride along,
		// otherwise synthesize a minimal permission object with the right sender mode.
		const policyOwnerOption = defaultPermissions.find(x => x.sender?.mode === Api.SendEmailFrom.PolicyOwner) ?? {
			sender: { mode: Api.SendEmailFrom.PolicyOwner },
		};
		sendOnBehalfOptions.push({
			text: 'Policy Owner',
			value: policyOwnerOption,
		});

		const policyCsrOption = defaultPermissions.find(x => x.sender?.mode === Api.SendEmailFrom.PolicyCsr) ?? {
			sender: { mode: Api.SendEmailFrom.PolicyCsr },
		};
		sendOnBehalfOptions.push({
			text: 'Policy CSR',
			value: policyCsrOption,
		});

		const policyProducerOption = defaultPermissions.find(x => x.sender?.mode === Api.SendEmailFrom.PolicyProducer) ?? {
			sender: { mode: Api.SendEmailFrom.PolicyProducer },
		};
		sendOnBehalfOptions.push({
			text: 'Policy Producer',
			value: policyProducerOption,
		});
	}

	const selectedUserOption = defaultPermissions.find(x => x.sender?.mode === Api.SendEmailFrom.SelectedUser);
	/** @NOTE - Only allow 'select an employee' if the contact selection was not individually selected */
	if (selectedUserOption && !props.disabled) {
		sendOnBehalfOptions.push({
			text: 'Select an employee',
			value: selectedUserOption,
		});
	}

	if (props.allowedContactBasedOptions === 'all') {
		userSession.customSendOnBehalfPermissions
			?.filter(x => !props.hideOptionsWithFilters || !x.requiredFilter)
			.forEach(x => {
				sendOnBehalfOptions.push({
					text: x.title,
					value: x,
				});
			});
	}
	return sendOnBehalfOptions;
};

const getSendOnBehalfAnchorText = (
	selectedOption: ISendOnBehalfOption,
	sendOptions: ISendOnBehalfOption[],
	connectionTypes: Api.IConnectionType[],
	selectedEmployee?: Api.IUser
) => {
	if (selectedOption?.value?.sender?.mode === Api.SendEmailFrom.ConnectionType) {
		return `${
			connectionTypes.find(connectionType => connectionType.value === selectedOption.value.sender.connectionType).label
		} for the recipient`;
	}

	if (
		selectedOption?.value?.sender &&
		selectedOption.value.sender.mode === Api.SendEmailFrom.SelectedUser &&
		selectedEmployee
	) {
		return getDisplayName(selectedEmployee);
	}

	if (selectedOption?.text) {
		return selectedOption.text;
	}
	return sendOptions.find(x => x.value.sender?.mode === Api.SendEmailFrom.CurrentUser)?.text;
};

/**
 * Employee picker for "send on behalf of". Loads all account users (cached, recursive) client-side.
 * Active users are selectable; not-yet-activated (INIT) users are shown but disabled (greyed out with
 * a "Not activated yet" note), since only active users can actually be sent on behalf of. Rendered
 * inside the dialog's (keepMounted=false) popup, so the fetch only fires when the picker is opened.
 *
 * inject(ImpersonationContextKey) mirrors the autocomplete field this replaces: in admin-impersonation
 * composer flows (e.g. MultiEmailScheduler) the ambient impersonationContext scopes the user list to
 * the impersonated account rather than the admin's own.
 */
const _SelectEmployeeCombobox = ({
	onSelect,
	selectedUserId,
	inputId,
	impersonationContext,
}: {
	onSelect: (user: Api.IUser) => void;
	/** Shows this user as the current selection, for an inline picker. */
	selectedUserId?: string;
	inputId?: string;
} & IImpersonationContextComponentProps) => {
	const { users, isLoading } = useLoadAllUsers({ impersonationContext });
	const employees = React.useMemo(() => {
		const eligible = users.filter(
			user =>
				user.activationStatus === Api.ActivationStatus.ACTIVE || user.activationStatus === Api.ActivationStatus.INIT
		);
		// Selectable (active) users first; not-yet-activated (INIT) users are listed last and rendered
		// disabled, since only active users can be sent on behalf of.
		return eligible.sort(
			(a, b) =>
				(a.activationStatus === Api.ActivationStatus.ACTIVE ? 0 : 1) -
				(b.activationStatus === Api.ActivationStatus.ACTIVE ? 0 : 1)
		);
	}, [users]);

	const selectedUser = selectedUserId ? (employees.find(user => user.id === selectedUserId) ?? null) : undefined;

	return (
		<Combobox
			items={employees}
			{...(selectedUser !== undefined ? { value: selectedUser } : {})}
			itemToStringLabel={item => getDisplayName(item as Api.IUser)}
			isItemEqualToValue={(a, b) => (a as Api.IUser)?.id === (b as Api.IUser)?.id}
			filter={(item, query) => {
				const trimmed = query.trim().toLowerCase();
				// The input shows the selected name, which would otherwise hide everyone else until it's cleared
				if (!trimmed || (selectedUser && trimmed === getDisplayName(selectedUser).toLowerCase())) {
					return true;
				}
				const user = item as Api.IUser;
				return (
					getDisplayName(user).toLowerCase().includes(trimmed) || (user.email ?? '').toLowerCase().includes(trimmed)
				);
			}}
			onValueChange={value => {
				const user = value as Api.IUser | null;
				// Disabled (not-yet-activated) users can't be chosen via the UI, but guard anyway so we
				// never send on behalf of an unactivated user.
				if (user?.activationStatus === Api.ActivationStatus.ACTIVE) {
					onSelect(user);
				}
			}}
		>
			<ComboboxInput id={inputId} placeholder='Search employee' styles={[styleSheet.searchEmployeeBox]} />
			<ComboboxContent styles={[styleSheet.popoverStyles]}>
				{isLoading ? (
					<ComboboxStatus>Loading employees…</ComboboxStatus>
				) : (
					<ComboboxEmpty>No employees found</ComboboxEmpty>
				)}
				<ComboboxList>
					{(user: Api.IUser) => {
						const isPending = user.activationStatus === Api.ActivationStatus.INIT;
						return (
							<ComboboxAvatarItem key={user.id} value={user} disabled={isPending}>
								<ComboboxAvatarItemAvatar
									imageUrl={user.profilePic}
									fallbackText={(user.firstName?.substring(0, 1) ?? '') + (user.lastName?.substring(0, 1) ?? '')}
								/>
								<ComboboxAvatarItemTitle>{getDisplayName(user)}</ComboboxAvatarItemTitle>
								{(user.email || isPending) && (
									<ComboboxAvatarItemDescription>
										{isPending ? <span className={css(styleSheet.pendingBadge)}>Pending</span> : null}
										{user.email}
									</ComboboxAvatarItemDescription>
								)}
							</ComboboxAvatarItem>
						);
					}}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

export const SelectEmployeeCombobox = inject(ImpersonationContextKey)(observer(_SelectEmployeeCombobox));

const _SendFromDropdown = ({
	allowedContactBasedOptions = 'none',
	impersonationContext,
	...rest
}: ISendFromDropdownProps & IImpersonationContextComponentProps) => {
	const props = {
		allowedContactBasedOptions,
		...rest,
	};
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const connectionTypeQuery = useConnectionTypes({
		enabled: userSession.account.features.connectionTypes?.enabled ?? false,
		refetchOnWindowFocus: false,
	});
	const connectionTypes = connectionTypeQuery.data?.availableConnectionTypes;
	// Scoped to the impersonated user so the picker reflects the customer's mailboxes, not the admin's.
	const sharedMailboxQuery = useSharedMailboxesQuery({
		impersonationContext,
		meta: { errorLogging: { category: 'SendFromDropdown', action: 'SharedMailboxQuery-Error' } },
	});
	const sharedMailboxes = sharedMailboxQuery.data;
	const hasSharedMailboxes =
		(sharedMailboxes?.available === false ? [] : (sharedMailboxes?.mailboxes ?? [])).length > 0;
	const primaryAddress = getPrimarySendFromAddress(userSession?.user);
	const sendOptions = getSendOptions(props, userSession, connectionTypes, sharedMailboxes, primaryAddress);

	// Primary and shared items share the CurrentUser permission, so disambiguate them by `sendFromEmailAddress`.
	const findSelectedOption = React.useCallback(
		(options: ISendOnBehalfOption[]) =>
			options.find(x => {
				if (!equal(x.value, props.sendFromOption)) {
					return false;
				}
				if (hasSharedMailboxes && x.value.sender?.mode === Api.SendEmailFrom.CurrentUser) {
					return (x.sendFromEmailAddress ?? undefined) === (props.sendFromEmailAddress ?? undefined);
				}
				return true;
			}),
		[props.sendFromOption, props.sendFromEmailAddress, hasSharedMailboxes]
	);

	const [selectedOption, setSelectedOption] = React.useState<ISendOnBehalfOption | undefined>(
		findSelectedOption(sendOptions)
	);
	const [selectAnEmployeeModalIsOpen, setSelectAnEmployeeModalIsOpen] = React.useState(false);
	const [selectedEmployee, setSelectedEmployee] = React.useState<Api.IUser>(null);
	const userQuery = useUserQuery({
		enabled: Boolean(props.sendFromUserId),
		id: props.sendFromUserId ?? '',
		meta: {
			errorLogging: {
				category: 'SendFromDropdown',
				action: 'UserQuery-Error',
			},
		},
	});
	const customSendUserQuery = useUserQueries({
		ids: sendOptions.filter(x => !!x.value.sender?.selectedUser).map(x => x.value.sender.selectedUser),
	});

	React.useEffect(() => {
		if (userQuery.data) {
			setSelectedEmployee(userQuery.data);
		}
	}, [userQuery.data]);

	// Adding, removing, or renaming a mailbox changes the options even while the count stays > 0, so key on
	// the mailboxes' identity rather than just their existence.
	const sharedMailboxesKey = (sharedMailboxes?.available === false ? [] : (sharedMailboxes?.mailboxes ?? []))
		.map(x => `${x.id}:${x.emailAddress}:${x.name ?? ''}:${x.verified}`)
		.join(',');
	React.useEffect(() => {
		setSelectedOption(findSelectedOption(sendOptions));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		props.sendFromOption,
		props.sendFromEmailAddress,
		hasSharedMailboxes,
		sharedMailboxesKey,
		sharedMailboxes?.reconnectRequired,
		primaryAddress,
	]);

	const sendOnBehalfSelectEmployee = () => {
		setSelectAnEmployeeModalIsOpen(true);
	};

	const onSendFromOptionChanged = async (option: ISendOnBehalfOption) => {
		if (option === selectedOption) {
			return;
		}

		props.onSendFromEmailAddressChange?.(option.sendFromEmailAddress);

		if (option.value.sender?.mode === Api.SendEmailFrom.SelectedUser && option.value.sender.selectedUser) {
			const user = customSendUserQuery.map(x => x.data).find(x => x.id === option.value.sender.selectedUser);
			if (user) {
				setSelectedEmployee(user);
				props.sendOnBehalfOf(option?.value, user);
			} else {
				errorMessages.pushApiError({ systemMessage: 'Failed to load user.' });
			}
		} else if (option.value.sender?.mode === Api.SendEmailFrom.SelectedUser) {
			sendOnBehalfSelectEmployee();
		} else {
			setSelectedOption(option);
			props.sendOnBehalfOf(option?.value);
		}
	};

	const onEmployeeSelected = (user: Api.IUser) => {
		const option = sendOptions.find(x => x.value.sender?.mode === Api.SendEmailFrom.SelectedUser);
		setSelectedOption(option);
		setSelectedEmployee(user);
		props.sendOnBehalfOf(option?.value, user);
		setSelectAnEmployeeModalIsOpen(false);
	};

	if (props.onRenderSendFromOptions) {
		const options = props.onRenderSendFromOptions();

		if (options) {
			return (
				<div className={css(baseStyleSheet.horizontalStack, styleSheet.sendFromDropdownContainer, props.styles)}>
					<span>Send from:</span>
					{options}
				</div>
			);
		}
	}

	if (!props.canSendOnBehalf && !hasSharedMailboxes) {
		return null;
	}

	return (
		<div className={css(styleSheet.sendFromDropdownContainer, props.styles)}>
			<span>Send from:</span>
			<Dropdown disabled={props.disabled}>
				<DropdownSelectTrigger styles={[styleSheet.dropdownAnchor]}>
					{getSendOnBehalfAnchorText(selectedOption, sendOptions, connectionTypes, selectedEmployee)}
				</DropdownSelectTrigger>
				<DropdownContent>
					{Object.values(sendOptions).map((x, i) => {
						return (
							<DropdownItem key={i} disabled={x.disabled} onClick={() => onSendFromOptionChanged(x)}>
								{x.text}
								{x.isSharedMailbox && x.disabled ? (
									<span className={css(styleSheet.reconnectHint)}> — finish setup in Settings</span>
								) : null}
							</DropdownItem>
						);
					})}
				</DropdownContent>
			</Dropdown>
			{props.canSendOnBehalf && (
				<Dialog open={selectAnEmployeeModalIsOpen} onOpenChange={setSelectAnEmployeeModalIsOpen}>
					<DialogPopup>
						<DialogHeader>
							<DialogTitle>Select an Employee</DialogTitle>
							<DialogDescription>
								Since you are the admin of this Levitate account, you can select another employee so this email will
								come from their email instead. They must be a Levitate user.
							</DialogDescription>
						</DialogHeader>
						<SelectEmployeeCombobox onSelect={onEmployeeSelected} />
					</DialogPopup>
				</Dialog>
			)}
		</div>
	);
};

export const SendFromDropdown = inject(ImpersonationContextKey)(observer(_SendFromDropdown));
